import { useEffect, useMemo, useRef, useState } from 'react';
import { syntheticEntryQuery } from '../../../shared/content-media/content-media';
import { ContentEditor, ContentViewer } from '../../../shared/content-media/ContentMedia';
import type {
  ClaimsInput,
  ProductModelView,
} from '../../../shared/product-review/adapters/product-review-port';
import { currentSubmissionReviews } from '../../../shared/product-review/consumer/submitted-content';
import { useSynLive } from '../../../shared/product-review/session/ProductReviewSession';
import '../../../shared/product-review/ui/product-review.css';
import type { ProductDraftErrors, ProductDraftFields } from '../domain/product-form';
import { emptyProductDraft, productDemoCategories } from '../domain/product-form';
import { PROVIDER_BASE } from '../routes';
import { ProductModelEditor } from './ProductModelEditor';

const SYN_STATUS_LABELS: Record<string, string> = {
  DRAFT: '草稿',
  SUBMITTED: '审核中',
  PUBLISHED: '已发布',
};

/**
 * The synthetic scope the running server is configured with. These are the values the 06 Owner
 * checks a create against; they are sent because the wire contract requires them, and the server
 * still decides. Changing them here grants nothing — an out-of-scope create is refused with 403.
 */
const SYN_SCOPE_COUNTRY = 'SYN-COUNTRY';

const SYN_SCOPE_SUPPLIER = 'SYN-SUPPLIER';

/** The four claims the durable 06 contract carries, mapped onto the confirmed editor's fields. */
function editorFieldsFrom(product: ProductModelView | null): ProductDraftFields {
  const base = emptyProductDraft();
  if (!product) return base;
  return {
    ...base,
    name: product.claims.modelName,
    category: product.claims.category,
    claims: product.claims.manufacturerClaims,
    source: product.claims.source,
  };
}

function claimsFrom(fields: ProductDraftFields): ClaimsInput {
  return {
    modelName: fields.name.trim(),
    category: fields.category,
    manufacturerClaims: fields.claims,
    source: fields.source,
  };
}

function validateLiveClaims(claims: ClaimsInput): ProductDraftErrors {
  const errors: ProductDraftErrors = {};
  if (!claims.modelName) errors.name = '产品名称与型号必填。';
  if (!claims.category) errors.category = '产品类别必填。';
  if (!claims.manufacturerClaims) errors.claims = '厂家声明必填。';
  if (!claims.source) errors.source = '资料来源必填。';
  return errors;
}

function Status({ status }: { readonly status: string }) {
  return (
    <span className={`provider-status provider-status-${SYN_STATUS_TONE[status] ?? 'gray'}`}>
      {SYN_STATUS_LABELS[status] ?? status}
    </span>
  );
}

/** 06 产品管理 — the confirmed list-and-detail page, driven by durable 06 reads. */
export function SynLiveProductManagement({ id }: { readonly id?: string | undefined }) {
  const live = useSynLive();
  const [readId, setReadId] = useState('');
  const [newId, setNewId] = useState('');
  const [editing, setEditing] = useState<null | 'new' | 'revise'>(null);
  const [contentJson, setContentJson] = useState('');
  const [mediaBusy, setMediaBusy] = useState(false);
  const product = live.product;
  const lastRead = useRef('');
  const shownId = useRef('');

  const readProduct = (value: string) => {
    if (!live.port || !value) return;
    void live.run(
      '数据库读回',
      async () => {
        const loaded = await live.port!.read({ session: live.session, productId: value });
        if (loaded.kind !== 'OK') return loaded;
        const reviews = await live.port!.getProductReviews({
          session: live.session,
          productId: value,
        });
        return reviews.kind === 'OK'
          ? { kind: 'OK' as const, value: { product: loaded.value, reviews: reviews.value } }
          : reviews;
      },
      (value2) => {
        live.applyProduct(value2.product);
        live.rememberReviews(value2.reviews);
      },
    );
  };

  useEffect(() => {
    if (!id) return;
    if (shownId.current !== id) {
      // The pane must never show one product's facts under another product's route. Dropping the
      // snapshot the moment the id changes means a failed read shows "not read" rather than stale
      // numbers, and a write issued in that window cannot target the product that used to be shown.
      shownId.current = id;
      live.forgetProduct();
    }
    if (!live.port || lastRead.current === id) return;
    lastRead.current = id;
    readProduct(id);
    // The port identity changes only on login/logout; `readProduct` is intentionally render-local.
  }, [id, live.port]);

  useEffect(() => {
    // A dialog that survives a logout would silently remount after the next login, showing an empty
    // new-product form where the operator last had a revision in progress.
    if (!live.auth) setEditing(null);
  }, [live.auth]);

  const saveDraft = (fields: ProductDraftFields, submitting: boolean): ProductDraftErrors => {
    if (!live.port) {
      setEditing(null);
      live.setNotice('会话已失效，编辑器已关闭。请重新登录后再保存。');
      return {};
    }
    const claims = { ...claimsFrom(fields), ...(contentJson ? { contentJson } : {}) };
    const errors = validateLiveClaims(claims);
    if (Object.keys(errors).length) return errors;
    const target = editing === 'revise' && product ? product : null;
    const createdId = newId.trim();
    if (!target && !createdId) return { name: '新产品编号必填。' };
    live.mutate(
      submitting ? '保存并提交审核' : '保存产品',
      async (idempotencyKey) => {
        const saved = target
          ? await live.port!.revise({
              session: live.session,
              idempotencyKey,
              productId: target.productId,
              expectedVersion: target.version,
              claims,
            })
          : await live.port!.createDraft({
              session: live.session,
              idempotencyKey,
              productId: createdId,
              // Country and supplier are the server-side synthetic identity scope, not caller input:
              // the 06 Owner refuses a create outside the scope the session actually holds.
              country: SYN_SCOPE_COUNTRY,
              supplier: SYN_SCOPE_SUPPLIER,
              claims,
            });
        if (saved.kind !== 'OK') return saved;
        if (!submitting) return saved;
        const submitted = await live.port!.submit({
          session: live.session,
          idempotencyKey: `${idempotencyKey}-submit`,
          productId: saved.value.productId,
          expectedVersion: saved.value.version,
        });
        if (submitted.kind !== 'OK') return submitted;
        // Read the durable product back rather than presenting the submission response as one: the
        // submit answer is a revision reference, and only a read is a product fact.
        return live.port!.read({ session: live.session, productId: saved.value.productId });
      },
      (value) => {
        live.applyProduct(value);
        setEditing(null);
        if (typeof window !== 'undefined') {
          window.history.replaceState(
            null,
            '',
            `${PROVIDER_BASE}/products/${value.productId}${syntheticEntryQuery()}`,
          );
        }
      },
    );
    return {};
  };

  const resume = (decisionId: string) => {
    if (!product) return;
    live.mutate(
      '恢复修改',
      (idempotencyKey) =>
        live.port!.resume({
          session: live.session,
          idempotencyKey,
          productId: product.productId,
          expectedVersion: product.version,
          decisionId,
        }),
      live.applyProduct,
    );
  };

  const submitCurrent = () => {
    if (!product) return;
    live.mutate(
      '提交审核',
      (idempotencyKey) =>
        live.port!.submit({
          session: live.session,
          idempotencyKey,
          productId: product.productId,
          expectedVersion: product.version,
        }),
      () => {
        live.forgetProduct();
        live.setNotice('服务端已确认提交。请数据库读回不可变提交版本，再由管理员受理。');
      },
    );
  };

  const reviews =
    product && live.productReviews ? currentSubmissionReviews(product, live.productReviews) : [];

  return (
    <>
      <nav className="provider-breadcrumb" aria-label="面包屑">
        <a href={`${PROVIDER_BASE}${syntheticEntryQuery()}`}>⌂ 工作台</a>
        <span>/</span>
        <a href={`${PROVIDER_BASE}/products${syntheticEntryQuery()}`}>我的产品</a>
        {id && (
          <>
            <span>/</span>
            <strong>{product?.claims.modelName ?? id}</strong>
          </>
        )}
      </nav>
      <div className="provider-products-layout">
        <aside className="provider-product-list portal-card" aria-label="我的产品列表">
          <div className="provider-list-heading">
            <h2>
              我的产品 <small>（本页会话索引）</small>
            </h2>
            <button
              className="portal-button"
              disabled={live.blocked || !live.auth || !newId.trim()}
              onClick={() => {
                setContentJson('');
                setEditing('new');
              }}
            >
              ＋ 新增产品
            </button>
          </div>
          <label className="provider-syn-new">
            新产品编号
            <input
              aria-label="新产品编号"
              value={newId}
              onChange={(event) => setNewId(event.target.value)}
            />
          </label>
          <label className="provider-search">
            <span aria-hidden="true">⌕</span>
            <input
              type="search"
              aria-label="按精确产品编号读取"
              placeholder="输入精确产品编号…"
              value={readId}
              onChange={(event) => setReadId(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') readProduct(readId.trim());
              }}
            />
          </label>
          <button
            className="portal-button provider-syn-read"
            disabled={live.readBlocked || !readId.trim()}
            onClick={() => readProduct(readId.trim())}
          >
            按编号读取数据库
          </button>
          <div className="provider-products-scroll">
            {live.knownIds.map((value) => (
              <a
                className={`provider-list-product ${value === id ? 'is-selected' : ''}`}
                key={value}
                href={`${PROVIDER_BASE}/products/${value}${syntheticEntryQuery()}`}
                aria-current={value === id ? 'page' : undefined}
              >
                <div className="provider-image-placeholder">
                  <small>数据库</small>
                </div>
                <div>
                  <strong>{value}</strong>
                  <span>{value === product?.productId ? `CAS ${product.version}` : '未读取'}</span>
                  <span>
                    {value === product?.productId
                      ? (SYN_STATUS_LABELS[product.state] ?? product.state)
                      : '—'}
                  </span>
                </div>
              </a>
            ))}
            {live.knownIds.length === 0 && (
              <div className="provider-empty">
                <h3>本页尚未读取任何产品</h3>
                <p>
                  06 Owner
                  目前没有产品列表契约，因此本列表是本页会话内已精确读取过的编号索引，不是数据库全量列表。
                </p>
              </div>
            )}
          </div>
          <p className="provider-fixture-note">
            无 06 列表契约 · 仅按精确产品编号读取 · 不连接 05 资产
          </p>
        </aside>
        {!product ? (
          <article className="provider-product-detail portal-card provider-empty">
            <h1>
              {!id ? '请选择一个产品' : live.blocked ? '读取中或结果未确认' : '尚未读取该产品'}
            </h1>
            <p>
              {!id
                ? '从左侧精确编号读取数据库记录，或新建一个 SYN 产品草稿。'
                : '业务事实只能来自服务端读回；本页不会用本地内容代替。'}
            </p>
            {id && (
              <button
                className="portal-button"
                disabled={live.readBlocked}
                onClick={() => readProduct(id)}
              >
                数据库读回 {id}
              </button>
            )}
          </article>
        ) : (
          <article className="provider-product-detail portal-card">
            <div className="provider-detail-top">
              <div className="provider-gallery">
                <div className="provider-image-placeholder provider-main-image">
                  <small>SYN 合成资料 · 主图及材料见版本资料</small>
                </div>
              </div>
              <div>
                <h1>{product.claims.modelName}</h1>
                <p className="provider-product-meta">
                  {product.claims.category} · 编号 {product.productId}
                </p>
                <Status status={product.state} />
                <dl className="provider-detail-facts">
                  <div>
                    <dt>CAS 版本</dt>
                    <dd>{product.version}</dd>
                  </div>
                  <div>
                    <dt>国家 / 厂商</dt>
                    <dd>
                      {product.country} / {product.supplier}
                    </dd>
                  </div>
                  <div>
                    <dt>当前摘要</dt>
                    <dd style={{ wordBreak: 'break-all' }}>{product.claims.digest}</dd>
                  </div>
                  <div>
                    <dt>退回决定</dt>
                    <dd>{product.returnDecisionId ?? '无'}</dd>
                  </div>
                </dl>
              </div>
            </div>
            <section>
              <h2>厂家声明与来源</h2>
              <dl className="provider-detail-facts">
                <div>
                  <dt>厂家声明（未经独立验证）</dt>
                  <dd style={{ whiteSpace: 'pre-wrap' }}>{product.claims.manufacturerClaims}</dd>
                </div>
                <div>
                  <dt>资料来源</dt>
                  <dd style={{ whiteSpace: 'pre-wrap' }}>{product.claims.source}</dd>
                </div>
              </dl>
            </section>
            <section>
              <ContentViewer
                owner="06"
                auth={{ session: live.session }}
                reference={{
                  subjectId: product.productId,
                  revision: product.version,
                  digest: product.claims.digest,
                  view: 'draft',
                }}
              />
              {product.publications.map((p) => (
                <ContentViewer
                  key={p.publishedVersion}
                  owner="06"
                  auth={{ session: live.session }}
                  title={`已发布版本 ${p.publishedVersion} 的资料`}
                  reference={{
                    subjectId: product.productId,
                    revision: p.publishedVersion,
                    digest: p.digest,
                    view: 'published',
                  }}
                />
              ))}
              <h2>操作</h2>
              <button
                className="portal-button"
                disabled={live.blocked || product.state !== 'DRAFT'}
                onClick={() => {
                  setReadId(product.productId);
                  setContentJson(product.claims.contentJson ?? '');
                  setEditing('revise');
                }}
              >
                修订产品资料
              </button>{' '}
              <button
                className="portal-button"
                disabled={live.blocked || product.state !== 'DRAFT'}
                onClick={submitCurrent}
              >
                提交审核
              </button>{' '}
              <button
                className="portal-button product-demo-secondary"
                disabled={live.readBlocked}
                onClick={() => readProduct(product.productId)}
              >
                数据库读回
              </button>
              {product.state !== 'DRAFT' && (
                <p className="provider-boundary-note">
                  当前状态为 {SYN_STATUS_LABELS[product.state] ?? product.state}
                  ，只有在草稿状态才允许修订或提交。
                </p>
              )}
            </section>
            <SynLiveMySubmissions />
            {product.state === 'SUBMITTED' && (
              <section>
                <h2>审核反馈</h2>
                {reviews.length === 0 ? (
                  <p>该精确提交尚无可见审核记录。</p>
                ) : (
                  reviews.map((review) => (
                    <div key={review.caseId} className="product-demo-feedback">
                      <strong>
                        审核 {review.caseId} · 修订 {review.submitted.revision} · 决定{' '}
                        {review.decision?.outcome ?? '待审核'}
                      </strong>
                      <p style={{ whiteSpace: 'pre-wrap' }}>
                        审核意见：{review.decision?.reason ?? '无'}
                      </p>
                      {review.decision?.outcome === 'CHANGES_REQUESTED' && (
                        <button
                          className="portal-button"
                          disabled={live.blocked}
                          onClick={() => resume(review.decision!.decisionId)}
                        >
                          按此退回意见恢复修改
                        </button>
                      )}
                    </div>
                  ))
                )}
              </section>
            )}
          </article>
        )}
      </div>
      {/* The editor holds its own open state, so it is unmounted the moment there is no session:
          a dialog left open across a logout would otherwise keep enabled write controls on screen. */}
      {editing && live.auth && (
        <ProductModelEditor
          mode="SYN_LIVE"
          busy={live.blocked || mediaBusy}
          contentEditor={
            <ContentEditor
              owner="06"
              auth={live.auth}
              value={contentJson}
              onChange={setContentJson}
              onBusy={setMediaBusy}
              reference={
                editing === 'revise' && product
                  ? {
                      subjectId: product.productId,
                      revision: product.version,
                      digest: product.claims.digest,
                      view: 'draft',
                    }
                  : undefined
              }
            />
          }
          live={{
            productId: editing === 'revise' && product ? product.productId : newId.trim(),
            version: editing === 'revise' && product ? product.version : null,
            state: editing === 'revise' && product ? product.state : null,
            digest: editing === 'revise' && product ? product.claims.digest : null,
          }}
          context={{
            id: editing === 'revise' && product ? product.productId : newId.trim(),
            fields:
              editing === 'revise' && product ? editorFieldsFrom(product) : editorFieldsFrom(null),
            baseline: editing === 'revise' && product ? editorFieldsFrom(product) : null,
            fixtureId: null,
          }}
          records={[]}
          onClose={() => setEditing(null)}
          onSave={saveDraft}
        />
      )}
    </>
  );
}

/** 06 我的提交 — the confirmed submissions-and-feedback section, driven by durable 06 reads. */
export function SynLiveMySubmissions() {
  const live = useSynLive();
  const product = live.product;
  const submissions = product?.submissions ?? [];
  const publications = product?.publications ?? [];
  const [filter, setFilter] = useState('全部');
  const reviews = useMemo(
    () => (product && live.productReviews ? live.productReviews : []),
    [product, live.productReviews],
  );
  const visible = submissions.filter((item) => {
    if (filter === '全部') return true;
    const decided = reviews.find((review) => review.submitted.revision === item.revision);
    if (filter === '待审核') return !decided?.decision;
    if (filter === '退回修改') return decided?.decision?.outcome === 'CHANGES_REQUESTED';
    if (filter === '审核通过') return decided?.decision?.outcome === 'ACCEPTED';
    return true;
  });
  return (
    <section className="product-demo-submissions" aria-label="我的提交">
      <div className="product-demo-section-heading">
        <h2>我的提交</h2>
        <label>
          状态
          <select
            aria-label="筛选提交状态"
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
          >
            {['全部', '待审核', '退回修改', '审核通过'].map((value) => (
              <option key={value}>{value}</option>
            ))}
          </select>
        </label>
      </div>
      <p className="product-demo-help">
        提交版本由 06 Owner 持久保存且不可修改；审核结论来自 08 记录。列表只在读取产品后显示。
      </p>
      {!product ? (
        <div className="provider-empty">
          <h3>尚未读取产品</h3>
          <p>请先在产品管理中按精确编号读回数据库记录。</p>
        </div>
      ) : visible.length === 0 ? (
        <div className="provider-empty">
          <h3>暂无匹配的提交版本</h3>
          <p>草稿尚未提交审核时不会出现在这里。</p>
        </div>
      ) : (
        visible.map((item) => {
          const decided = reviews.find((review) => review.submitted.revision === item.revision);
          const published = publications.find(
            (entry) => entry.submissionRevision === item.revision,
          );
          return (
            <article className="product-demo-submission-card" key={item.revision}>
              <header>
                <div>
                  <h3>
                    修订 {item.revision} · {product.claims.modelName}
                  </h3>
                  <p>
                    提交账号 {item.makerAccount} · {item.submittedAt}
                  </p>
                </div>
                <span className="product-demo-status">
                  {decided?.decision
                    ? decided.decision.outcome === 'ACCEPTED'
                      ? '审核通过'
                      : '退回修改'
                    : '待审核'}
                </span>
              </header>
              <p className="product-demo-draft-id" style={{ wordBreak: 'break-all' }}>
                摘要：{item.digest}
              </p>
              {decided?.decision && (
                <section className="product-demo-feedback">
                  <strong>审核意见 · {decided.decision.decisionId}</strong>
                  <p>{decided.decision.reason}</p>
                  <p>决定时间：{decided.decision.decidedAt}</p>
                </section>
              )}
              <dl className="product-demo-record-data">
                <div>
                  <dt>发布版本</dt>
                  <dd>{published ? `v${published.publishedVersion}` : '尚未发布'}</dd>
                </div>
                <div>
                  <dt>发布账号</dt>
                  <dd>{published?.publisherAccount ?? '—'}</dd>
                </div>
              </dl>
            </article>
          );
        })
      )}
      {live.publication && (
        <section className="product-demo-submission-card">
          <header>
            <div>
              <h3>Owner 发布事实</h3>
              <p>
                发布版本 {live.publication.publishedVersion} · 提交修订{' '}
                {live.publication.submissionRevision} · 发布账号 {live.publication.publisherAccount}
              </p>
            </div>
          </header>
          <p className="product-demo-draft-id" style={{ wordBreak: 'break-all' }}>
            摘要：{live.publication.digest}
          </p>
          <div className="product-demo-record-actions">
            <button
              className="portal-button"
              disabled={live.readBlocked}
              onClick={() => {
                if (!live.port || !live.publication) return;
                void live.run(
                  '精确读取已发布产品',
                  () => live.port!.readExact({ session: live.session, ...live.publication! }),
                  live.setExact,
                );
              }}
            >
              精确读取已发布产品
            </button>
          </div>
          {live.exact && (
            <p>
              精确读回状态：{live.exact.status} ·{' '}
              {live.exact.publication
                ? `已确认版本 v${live.exact.publication.publishedVersion}`
                : '无已确认版本'}
            </p>
          )}
        </section>
      )}
    </section>
  );
}

export const synLiveProductCategories = productDemoCategories;
const SYN_STATUS_TONE: Record<string, string> = {
  DRAFT: 'gray',
  SUBMITTED: 'blue',
  PUBLISHED: 'green',
};
